import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button as AntButton,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Modal,
  Row,
  Upload,
} from "antd";
import type { RcFile, UploadFile } from "antd/es/upload/interface";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import type { Dayjs } from "dayjs";
import {
  useGetMyExitQuery,
  useGetResignationFormQuery,
  useSubmitResignationMutation,
  useUploadExitAttachmentMutation,
  useWithdrawExitMutation,
} from "../../../store/apis/exit.api";
import type { MyExit, SubmitResignationBody, TerminationRequestDto } from "../Utils/types";
import {
  formatExitDateDisplay,
  utcYmdFromPickerCurrent,
  validateExitLastWorkingDay,
  SEPARATION_DATE_DISPLAY_FORMAT,
} from "../Utils/exitDateUtils";
import { formLabelWithInlineHint } from "../Components/formLabelWithInlineHint";
import { separationFormRequiredMark } from "../Components/separationFormRequiredMark";
import { separationTextAreaClassName } from "../Utils/separationTextAreaClassName";
import { pickUploadedAttachmentFields } from "../Utils/attachmentUtils";
import {
  separationDocumentUploadItemRender,
  separationDocumentUploadListClassName,
  separationDocumentUploadSideBySideClassName,
} from "../Components/separationDocumentUploadItemRender";
import { getErrorMessage } from "../../../store/utils/apiError";
import { useAuth } from "../../../store/hooks/useAuth";
import { useMyExitListQueryGate } from "../Utils/useMyExitListQueryGate";
import {
  debugResignationFormExitIdShape,
  pickActiveExitIdFromResignationForm,
} from "../Utils/resignationFormActiveExit";
import { ConfigurableTable, ConfirmationModal, Select } from "../../common";
import {
  SeparationPageSkeleton,
  SeparationFormSkeleton,
  SeparationTableSkeleton,
} from "../Components/SeparationPageSkeleton";
import type { TableColumn } from "../../common/Table";
import SimpleTooltip from "../../common/SimpleTooltip";
import { useSearchParams } from "react-router-dom";
import { AlertCircle, Eye, RotateCcw } from "lucide-react";
import Button from "../../common/Button";
import {
  exitRecordBlocksNewSubmission,
  exitRecordWithdrawEligibility,
  selectPrimaryMyExit,
} from "../Utils/exitStatusUtils";
import { ExitLifecycleContent } from "../Lifecycle/ExitLifecyclePage";
import {
  exitListStatusLabel,
  exitWorkflowPhaseKey,
  lastWorkingDisplay,
  reasonDisplay,
  resignationReasonOptionLabel,
} from "../Utils/exitRecordDisplay";
import { SeparationStatusBadge } from "../Components/SeparationStatusBadge";
import { terminationListStatusLabel } from "../Utils/terminationStatusUtils";
import MyExitViewModal from "./components/MyExitViewModal";
import TabSelector from "../../employee-service/component/TabSelector";

dayjs.extend(utc);

const MAX_BYTES = 5 * 1024 * 1024;
const ACCEPT_EXT = /\.(pdf|doc|docx)$/i;

type FormValues = {
  reasonId: string;
  lastWorkingDay: Dayjs;
  comments?: string;
};

const ResignationPage: React.FC = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [form] = Form.useForm<FormValues>();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [attachmentPayload, setAttachmentPayload] = useState<
    Pick<
      SubmitResignationBody,
      "attachmentFileUrl" | "attachmentFileKey" | "attachmentOriginalName"
    >
  >({});
  const [viewExit, setViewExit] = useState<MyExit | null>(null);
  const [withdrawConfirmOpen, setWithdrawConfirmOpen] = useState(false);
  const [exitToWithdraw, setExitToWithdraw] = useState<MyExit | null>(null);

  const sessionReady = Boolean(user?.id);
  const { skipMyExit, rbacReady } = useMyExitListQueryGate();
  const {
    data: ctx,
    isLoading,
    isFetching,
    isError,
    refetch: refetchForm,
  } = useGetResignationFormQuery(undefined, { skip: !sessionReady });
  const {
    data: myExitList = [],
    isLoading: myExitLoading,
    isFetching: myExitFetching,
    isError: myExitError,
    refetch: refetchMyExit,
  } = useGetMyExitQuery(undefined, { skip: !sessionReady || skipMyExit });
  const [submitResignation, { isLoading: submitting }] =
    useSubmitResignationMutation();
  const [uploadAttachment, { isLoading: uploading }] =
    useUploadExitAttachmentMutation();
  const [withdrawExit, { isLoading: withdrawing }] =
    useWithdrawExitMutation();

  /** Newest submission first (then stable tie-break on id). */
  const appliedRows = useMemo(() => {
    const list = myExitList ?? [];
    const ts = (e: MyExit) =>
      Date.parse(e.createdAt ?? "") || Date.parse(e.updatedAt ?? "") || 0;
    return [...list].sort((a, b) => {
      const d = ts(b) - ts(a);
      if (d !== 0) return d;
      return String(b.id).localeCompare(String(a.id));
    });
  }, [myExitList]);
  const blockedByActiveExit = useMemo(
    () => myExitList.some((e) => exitRecordBlocksNewSubmission(e)),
    [myExitList],
  );
  /**
   * Exit lifecycle (checklist, interview) uses my-exit when available; otherwise
   * GET /v2/exit/resignation-form (`activeExitId`, `linkedExitId`, nested
   * `activeTermination.exitId`, …). When a termination is in progress, the form
   * payload is the source of truth for the **exit** id; prefer it over the list
   * so the checklist works for resignation-only roles and when my-exit lags.
   */
  const checklistExitId = useMemo(() => {
    const fromForm = pickActiveExitIdFromResignationForm(ctx);
    if (ctx?.activeTerminationInProgress && fromForm) {
      return fromForm;
    }
    const fromList = selectPrimaryMyExit(myExitList ?? []);
    if (fromList?.id) return fromList.id;
    return fromForm;
  }, [myExitList, ctx]);
  const separationTabs = useMemo(() => {
    const base = [{ k: "resign" as const, label: "Resignation" }];
    if (!checklistExitId) return base;
    return [
      ...base,
      { k: "checklist" as const, label: "Exit checklist & interview" },
    ];
  }, [checklistExitId]);

  const showExitChecklistTab = separationTabs.some((t) => t.k === "checklist");
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const list = myExitList ?? [];
    const primaryFromList = selectPrimaryMyExit(list);
    const fromForm = pickActiveExitIdFromResignationForm(ctx);
    const formShape = debugResignationFormExitIdShape(ctx);
    const fromListId =
      primaryFromList?.id != null ? String(primaryFromList.id) : undefined;
    const priorityTerminationForm =
      Boolean(ctx?.activeTerminationInProgress) && Boolean(fromForm);
    const derivedFrom = priorityTerminationForm
      ? "resignation-form (exit id, termination in progress — preferred)"
      : fromListId
        ? "my-exit (selectPrimaryMyExit)"
        : fromForm
          ? "resignation-form (activeExitId / linkedExitId / nested)"
          : "none (no id from my-exit or form)";
    // eslint-disable-next-line no-console
    console.log("[ResignationPage] exit checklist tab visibility", {
      showExitChecklistTab,
      checklistExitId: checklistExitId ?? null,
      derivedFrom,
      primaryFromList: primaryFromList
        ? {
          id: fromListId,
          exitStatus: (primaryFromList as MyExit).exitStatus,
          status: (primaryFromList as MyExit).status,
          exitInitiationSource: (primaryFromList as MyExit)
            .exitInitiationSource,
        }
        : null,
      fromForm: fromForm ?? null,
      formExitIdDebug: {
        topLevelStringKeys: formShape.topLevelStringKeys,
        nestedContainersPresent: formShape.nestedContainersPresent,
        hint:
          !fromListId && !fromForm && skipMyExit
            ? "Resignation-only: GET my-exit is skipped; the exit id must be present on GET resignation-form (activeExitId, linkedExitId, or nested). Ask backend to add the in-flight exit id to that response."
            : !fromListId && !fromForm
              ? "No exit id from my-exit or form; see topLevelStringKeys / backend contract."
              : null,
      },
      gating: {
        sessionReady,
        rbacReady,
        skipMyExit,
        getMyExitQuerySkipped: !sessionReady || skipMyExit,
        myExitLoading,
        myExitError,
        myExitListLength: list.length,
        formContextLoading: isLoading,
        formContextError: isError,
        hasResignationFormContext: Boolean(ctx),
        resignationDataReady:
          sessionReady &&
          rbacReady &&
          !isLoading &&
          (skipMyExit || !myExitLoading),
      },
    });
  }, [
    checklistExitId,
    ctx,
    isError,
    isLoading,
    myExitError,
    myExitList,
    myExitLoading,
    rbacReady,
    sessionReady,
    showExitChecklistTab,
    skipMyExit,
  ]);

  const resignationDataReady =
    sessionReady &&
    rbacReady &&
    !isLoading &&
    (skipMyExit || !myExitLoading);
  useEffect(() => {
    if (!resignationDataReady) return;
    if (checklistExitId) return;
    if (searchParams.get("tab") === "checklist") {
      setSearchParams({}, { replace: true });
    }
  }, [
    resignationDataReady,
    checklistExitId,
    searchParams,
    setSearchParams,
  ]);

  useEffect(() => {
    if (!resignationDataReady || !myExitList.length) return;
    const draft = myExitList.find((e) => e.status === "DRAFT");
    if (draft) {
      form.setFieldsValue({
        reasonId: draft.reasonId || draft.reason?.id,
        lastWorkingDay: draft.lastWorkingDayRequested
          ? dayjs(draft.lastWorkingDayRequested)
          : undefined,
        comments: draft.comments,
      });
    }
  }, [resignationDataReady, myExitList, form]);

  const canSubmit =
    ctx?.canSubmitNewRequest === true &&
    (skipMyExit ? true : !blockedByActiveExit);

  const terminationStatusLine = useMemo(() => {
    if (!ctx?.activeTerminationStatus) return null;
    return terminationListStatusLabel({
      id: String(ctx.activeTerminationId ?? "ctx"),
      terminationStatus: ctx.activeTerminationStatus,
    } as TerminationRequestDto);
  }, [ctx?.activeTerminationId, ctx?.activeTerminationStatus]);


  const reasonOptions = useMemo(() => ctx?.reasonOptions ?? [], [ctx]);

  const openView = useCallback((record: MyExit) => {
    setViewExit(record);
  }, []);

  const confirmWithdraw = useCallback(
    (record: MyExit) => {
      if (String(record.pullbackRequestStatus ?? "").toUpperCase() === "REQUESTED") {
        toast("Resignation pullback request has been submitted");
        return;
      }
      const eligibility = exitRecordWithdrawEligibility(record);
      if (eligibility.kind === "none") return;

      setExitToWithdraw(record);
      setWithdrawConfirmOpen(true);
    },
    [refetchForm, refetchMyExit, withdrawExit],
  );

  const appliedColumns: TableColumn<MyExit>[] = useMemo(
    () => [
      {
        key: "status",
        title: "Approval Status",
        label: "Approval Status",
        render: (_, record) => (
          <div className="flex flex-col items-start gap-1">
            <SeparationStatusBadge
              label={exitListStatusLabel(record)}
              phase={exitWorkflowPhaseKey(record)}
            />
            {/* {String(record.pullbackRequestStatus ?? "").toUpperCase() ===
            "REQUESTED" ? (
              <span className="text-xs text-amber-700">Pullback requested</span>
            ) : null} */}
          </div>
        ),
      },
      {
        key: "lwd",
        title: "Last working date",
        label: "Last working date",
        render: (_, record) => lastWorkingDisplay(record),
      },
      {
        key: "reason",
        title: "Reason",
        label: "Reason",
        render: (_, record) => reasonDisplay(record),
      },
      {
        key: "submitted",
        title: "Submitted on",
        label: "Submitted on",
        render: (_, record) => formatExitDateDisplay(record.createdAt),
      },
      {
        key: "actions",
        title: "Actions",
        label: "Actions",
        required: true,
        align: "center",
        width: 104,
        render: (_, record) => {
          const w = exitRecordWithdrawEligibility(record);
          const pullbackRequested =
            String(record.pullbackRequestStatus ?? "").toUpperCase() ===
            "REQUESTED";
          const pullbackDisabledReason =
            pullbackRequested && "reason" in w && typeof w.reason === "string"
              ? w.reason
              : "Awaiting manager/HR review.";
          const canWithdraw = w.kind === "free" || w.kind === "hr_gated";
          const actionIconBtn =
            "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-800";
          return (
            <div className="inline-flex items-center justify-center gap-1.5 flex-nowrap">
              <SimpleTooltip
                label="View"
                side="top"
                className="inline-flex shrink-0"
                tooltipClassName=" text-xs shadow-md border-0"
              >
                <button
                  type="button"
                  onClick={() => openView(record)}
                  aria-label="View"
                  className={actionIconBtn}
                >
                  <Eye className="w-4 h-4" aria-hidden="true" />
                </button>
              </SimpleTooltip>
              {canWithdraw || pullbackRequested ? (
                <SimpleTooltip
                  label={
                    pullbackRequested
                      ? pullbackDisabledReason
                      : w.kind === "hr_gated"
                        ? "Request pullback"
                        : "Withdraw resignation"
                  }
                  side="top"
                  className="inline-flex shrink-0"
                  tooltipClassName=" text-xs shadow-md border-0"
                >
                  <button
                    type="button"
                    aria-label={
                      pullbackRequested
                        ? "Pullback requested"
                        : w.kind === "hr_gated"
                          ? "Request pullback"
                          : "Withdraw resignation"
                    }
                    disabled={withdrawing || pullbackRequested}
                    onClick={() => {
                      if (pullbackRequested) return;
                      confirmWithdraw(record);
                    }}
                    className={
                      withdrawing
                        ? `${actionIconBtn} cursor-wait opacity-60 pointer-events-none`
                        : pullbackRequested
                          ? `${actionIconBtn} cursor-not-allowed opacity-60`
                          : actionIconBtn
                    }
                  >
                    <RotateCcw
                      className="w-4 h-4 text-primary-600"
                      aria-hidden="true"
                    />
                  </button>
                </SimpleTooltip>
              ) : null}
            </div>
          );
        },
      },
    ],
    [confirmWithdraw, openView, withdrawing],
  );

  const onFinish = async (values: FormValues) => {
    if (!ctx) return;
    if (!ctx.canSubmitNewRequest) {
      toast.error(
        "Submit resignation is not available right now. If a termination is in progress or you already have an active exit, contact HR.",
      );
      return;
    }
    const ymd = utcYmdFromPickerCurrent(values.lastWorkingDay);
    const lwdCheck = validateExitLastWorkingDay({
      resignationDateYmd: ctx.resignationDate,
      lastWorkingDayYmd: ymd,
      minNoticeEnforced: ctx.minNoticeEnforced,
      minNoticeCalendarDays: ctx.minNoticeCalendarDays,
    });
    if (!lwdCheck.ok) {
      toast.error(lwdCheck.message);
      return;
    }
    try {
      await submitResignation({
        reasonId: values.reasonId,
        lastWorkingDayRequested: `${ymd}T00:00:00.000Z`,
        comments: values.comments?.trim() || undefined,
        ...attachmentPayload,
      }).unwrap();
      toast.success("Resignation request has been submitted successfully");
      form.resetFields();
      setFileList([]);
      setAttachmentPayload({});
      if (!skipMyExit) void refetchMyExit();
      void refetchForm();
    } catch (e) {
      toast.error(getErrorMessage(e as never) ?? "The resignation submission could not be completed");
    }
  };

  const handleBeforeUpload = (file: RcFile) => {
    if (file.size > MAX_BYTES) {
      toast.error("File must be 5MB or smaller.");
      return Upload.LIST_IGNORE;
    }
    if (!ACCEPT_EXT.test(file.name)) {
      toast.error("Only PDF, DOC, or DOCX files are allowed.");
      return Upload.LIST_IGNORE;
    }
    void (async () => {
      try {
        const res = await uploadAttachment(file).unwrap();
        const fields = pickUploadedAttachmentFields(res);
        if (!fields.attachmentFileUrl && !fields.attachmentFileKey) {
          toast.error("Upload succeeded but no file reference was returned.");
          return;
        }
        setAttachmentPayload(fields);
        setFileList([
          {
            uid: file.uid,
            name: file.name,
            status: "done",
          },
        ]);
        toast.success("Supporting attachment uploaded successfully");
      } catch (e) {
        toast.error(getErrorMessage(e as never) ?? "The file upload could not be completed");
      }
    })();
    return false;
  };

  const dataLoading =
    !sessionReady ||
    !rbacReady ||
    isLoading ||
    isFetching ||
    (!skipMyExit && (myExitLoading || myExitFetching));

  // Very first mount — no session / RBAC yet — show full-page skeleton
  // (header included) because we can't even render tabs without ctx.
  if (dataLoading && !ctx) {
    return <SeparationPageSkeleton withForm={true} />;
  }

  const activeTab =
    searchParams.get("tab") === "checklist" && checklistExitId
      ? "checklist"
      : "resign";

  const resignTabContent = (
    <>
      <Card>
        {myExitError ? (
          <Alert
            type="warning"
            showIcon
            icon={<AlertCircle size={20} className="mt-1 text-amber-600" />}
            className="mb-4 border-amber-200 [&_*]:transition-none [&_*]:overflow-visible"
            message="Could not load your existing resignation request. You can still use the form if allowed."
          />
        ) : null}

        {!canSubmit ? (
          ctx?.activeTerminationInProgress ? (
            <Alert
              type="warning"
              showIcon
              icon={<AlertCircle size={20} className="mt-1 text-amber-600" />}
              className="mb-4 border-amber-200 [&_*]:transition-none [&_*]:overflow-visible"
              message="Termination in progress"
              description={
                <div className="space-y-2 text-sm">
                  {terminationStatusLine ? (
                    <p className="m-0 text-slate-600">Status: {terminationStatusLine}</p>
                  ) : null}
                </div>
              }
            />
          ) : (
            <Alert
              showIcon
              className="mb-4"
              message={
                <span className="text-base block mb-0.5">
                  {ctx?.activeExitStatus === "NOTICE_PERIOD"
                    ? "You are currently in your notice period"
                    : ctx?.activeExitStatus === "EXIT_IN_PROGRESS"
                      ? "Your exit is currently in progress"
                      : blockedByActiveExit
                        ? "You have an open resignation request"
                        : "Resignation submission is not available"}
                </span>
              }
              description={
                !blockedByActiveExit ? (
                  <div className="">
                    {ctx?.activeExitStatus ? (
                      <p className="m-0 flex items-center gap-2 text-sm">
                        <span className="opacity-80">Exit status:</span>
                        <span className="font-bold px-1.5 py-0.5 rounded text-xs">
                          {String(ctx?.activeExitStatus).replace(/_/g, " ")}
                        </span>
                      </p>
                    ) : null}
                    <p className="leading-relaxed text-sm">
                      {ctx?.activeExitStatusMessage ??
                        "You cannot submit a new resignation request right now because an existing process is already underway."}
                    </p>
                  </div>
                ) : (
                  <p className=" m-0 mt-1.5 text-sm text-slate-600">
                    You already have an active resignation request listed below. Please wait for its approval or withdraw it before submitting a new one.
                  </p>
                )
              }
            />
          )
        ) : null}

        <Form
          form={form}
          layout="vertical"
          onFinish={(v) => void onFinish(v)}
          requiredMark={separationFormRequiredMark}
        >
          <Row gutter={[16, 16]} className="mb-4">
            <Col xs={24} md={12}>
              <Form.Item
                name="reasonId"
                label="Reason"
                rules={[{ required: true, message: "Select a reason" }]}
                className="mb-0"
                getValueFromEvent={(v) => v}
              >
                <Select
                  placeholder="Select reason"
                  options={reasonOptions.map((o) => ({
                    value: o.id,
                    label: resignationReasonOptionLabel(o),
                  }))}
                  searchable
                  className="w-full"
                  disabled={!canSubmit}
                  clearable
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="lastWorkingDay"
                label={formLabelWithInlineHint(
                  "Last working date",
                  ctx?.minNoticeEnforced && ctx?.minNoticeCalendarDays
                    ? `(Min notice: ${ctx.minNoticeCalendarDays} days)`
                    : "",
                )}
                rules={[
                  { required: true, message: "Select last working date" },
                ]}
                className="mb-0"
              >
                <DatePicker
                  className="w-full"
                  format={SEPARATION_DATE_DISPLAY_FORMAT}
                  disabled={!canSubmit}
                  disabledDate={(current) => {
                    if (!current) return false;
                    const anchor = dayjs
                      .utc(ctx?.resignationDate, "YYYY-MM-DD")
                      .startOf("day");
                    const selectedUtc = dayjs
                      .utc(current.format("YYYY-MM-DD"))
                      .startOf("day");
                    if (ctx?.minNoticeEnforced && ctx?.minNoticeCalendarDays) {
                      const minLwd = anchor.add(ctx.minNoticeCalendarDays, "day");
                      return selectedUtc.isBefore(minLwd, "day");
                    }
                    return selectedUtc.isBefore(anchor, "day");
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="comments" label="Comments">
            <Input.TextArea
              className={separationTextAreaClassName}
              rows={3}
              maxLength={500}
              showCount
              placeholder="Comments (max 500 characters)"
              disabled={!canSubmit}
            />
          </Form.Item>

          <Form.Item
            label={formLabelWithInlineHint(
              "Supporting document",
              "(PDF, DOC, or DOCX. Maximum 5MB)",
            )}
            className="mb-0"
          >
            <Upload
              className={separationDocumentUploadSideBySideClassName}
              fileList={fileList}
              beforeUpload={handleBeforeUpload}
              itemRender={separationDocumentUploadItemRender}
              onRemove={() => {
                setFileList([]);
                setAttachmentPayload({});
              }}
              maxCount={1}
              accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              disabled={!canSubmit || uploading}
            >
              <AntButton disabled={!canSubmit || uploading}>
                {uploading ? "Uploading…" : "Upload file"}
              </AntButton>
            </Upload>
          </Form.Item>

          <div className="flex items-center gap-4 justify-end mt-4">
            <Button
              htmlType="submit"
              appearance="primary"
              size="large"
              loading={submitting}
              disabled={!canSubmit}
            >
              {submitting ? "Submitting…" : "Submit"}
            </Button>
          </div>
        </Form>
      </Card>

      <div className="bg-white rounded-xl shadow-soft border border-slate-100 overflow-hidden">
        <ConfigurableTable<MyExit>
          columns={appliedColumns}
          data={appliedRows}
          rowKey="id"
          emptyMessage="No resignation submitted yet."
          size="sm"
          bordered={true}
          hoverable
          className="[&_thead_th]:!text-slate-500 [&_th:first-child]:!pl-4 [&_th:first-child]:sm:!pl-6 [&_td:first-child]:!pl-4 [&_td:first-child]:sm:!pl-6 [&_th:last-child]:!pr-4 [&_th:last-child]:sm:!pr-6 [&_td:last-child]:!pr-4 [&_td:last-child]:sm:!pr-6"
          configOptions={{ persistenceKey: "separation-resignation-my-requests" }}
          spacing={0}
          renderColumnSelector={(selector) => (
            <div className="px-4 py-3 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3 bg-white">
              <h3 className="text-base font-semibold text-slate-900 m-0">
                Resignation Requests ({appliedRows.length})
              </h3>
              <div className="flex items-center gap-2 shrink-0">{selector}</div>
            </div>
          )}
        />
      </div>
    </>
  );

  const errorContent = (
    <div className="flex flex-col items-center justify-center py-20 px-6 text-center w-full grow min-h-[60vh]">
      <div className="w-20 h-20 bg-slate-50 text-slate-400 rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm border border-slate-100/80">
        <AlertCircle size={40} strokeWidth={1.5} />
      </div>
      <h2 className="text-3xl font-bold text-slate-900 mb-3 tracking-tight">Could not load resignation form</h2>
      <p className="text-slate-500 mb-8 text-lg max-w-md mx-auto leading-relaxed">
        We encountered a problem while fetching the resignation details. This could be due to a temporary connection issue. Please try again.
      </p>
      <AntButton
        type="primary"
        onClick={() => {
          void refetchForm();
          if (!skipMyExit) void refetchMyExit();
        }}
        className="mx-auto h-12 rounded-xl flex items-center justify-center gap-2 px-10 font-medium shadow-md hover:shadow-lg transition-all"
      >
        <RotateCcw size={20} />
        Retry
      </AntButton>
    </div>
  );

  return (
    <>
      <div className="bg-surface-muted px-4 sm:px-6 pt-6 pb-2 mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 m-0">
          Resignation
        </h1>
        {separationTabs.length > 1 ? (
          <div className="mt-4">
            <TabSelector
              tabs={separationTabs}
              active={activeTab}
              onChange={(key) => {
                if (key === "checklist") {
                  setSearchParams({ tab: "checklist" });
                } else {
                  setSearchParams({});
                }
              }}
            />
          </div>
        ) : null}
      </div>

      <div className="px-4 sm:px-6 pt-2 pb-8 max-w-full mx-auto w-full min-w-0 space-y-6">
        {activeTab === "checklist" && checklistExitId ? (
          <div className="w-full min-w-0">
            <ExitLifecycleContent
              exitId={checklistExitId}
              variant="employee"
              showHeaderLinks={false}
            />
          </div>
        ) : dataLoading ? (
          <>
            <SeparationFormSkeleton />
            <SeparationTableSkeleton rows={4} />
          </>
        ) : isError || !ctx ? (
          errorContent
        ) : (
          resignTabContent
        )}
      </div>

      <MyExitViewModal exit={viewExit} onClose={() => setViewExit(null)} />

      <ConfirmationModal
        isOpen={withdrawConfirmOpen}
        onClose={() => {
          setWithdrawConfirmOpen(false);
          setExitToWithdraw(null);
        }}
        onConfirm={async () => {
          if (!exitToWithdraw) return;
          const eligibility = exitRecordWithdrawEligibility(exitToWithdraw);
          const isFree = eligibility.kind === "free";
          try {
            await withdrawExit(exitToWithdraw.id).unwrap();
            toast.success(
              isFree
                ? "Resignation request has been withdrawn successfully"
                : "Pullback request submitted for approval.",
            );
            setWithdrawConfirmOpen(false);
            setExitToWithdraw(null);
            if (!skipMyExit) void refetchMyExit();
            void refetchForm();
          } catch (e) {
            toast.error(getErrorMessage(e as never) ?? "The resignation withdrawal could not be completed");
          }
        }}
        title={
          exitToWithdraw &&
          exitRecordWithdrawEligibility(exitToWithdraw).kind === "free"
            ? "Withdraw resignation?"
            : "Request pullback?"
        }
        message={
          exitToWithdraw &&
          exitRecordWithdrawEligibility(exitToWithdraw).kind === "free"
            ? "This resignation will be withdrawn immediately."
            : "This will submit a pullback request for manager/HR approval."
        }
        type={
          exitToWithdraw &&
          exitRecordWithdrawEligibility(exitToWithdraw).kind === "free"
            ? "danger"
            : "warning"
        }
        confirmText={
          exitToWithdraw &&
          exitRecordWithdrawEligibility(exitToWithdraw).kind === "free"
            ? "Withdraw resignation"
            : "Request pullback"
        }
        isLoading={withdrawing}
      />
    </>
  );
};

export default ResignationPage;
